import Logout from '@mui/icons-material/Logout'
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined'
import PersonIcon from '@mui/icons-material/Person'
import {
  Avatar,
  Badge,
  Box,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography
} from '@mui/material'
import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import logo from '../assets/images/logo.svg'
import { useAuthStore } from '../hooks/useAuthStore'
import { useSignOutMutation } from '../hooks/useMutations/useAuthMutations'
import { useNotifications, useUser } from '../hooks/useQueries/useUsers'
import { getAvatarName } from '../utils/user.utils'
import FundingLogos from './common/FundingLogos'
import ContainedAvatar from './ContainedAvatar'
import NotificationsMenu from './NotificationsMenu'

export default function Header() {
  const { t } = useTranslation()
  const { userId, accessToken } = useAuthStore()
  const { data: user, isLoading } = useUser(userId!)
  const { data: notifications } = useNotifications(userId!, {
    pageSize: '1',
    pageNumber: '1'
  })

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const [notificationsAnchorEl, setNotificationsAnchorEl] =
    useState<null | HTMLElement>(null)
  const isNotificationsMenuOpen = Boolean(notificationsAnchorEl)

  const signOutMutation = useSignOutMutation()

  if (isLoading || !user) {
    return <div>Loading...</div>
  }

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleOpenNotificationsMenu = (event: MouseEvent<HTMLElement>) => {
    setNotificationsAnchorEl(event.currentTarget)
  }

  const handleCloseNotificationsMenu = () => {
    setNotificationsAnchorEl(null)
  }

  return (
    <Paper
      sx={{
        width: '100%',
        backgroundColor: 'background.default',
        height: '7rem'
      }}
      elevation={0}
    >
      <Box
        sx={{
          margin: '0 auto',
          height: '100%',
          px: 3,
          width: { xs: '100%', md: 'auto' },
          display: 'flex',
          justifyContent: 'space-between'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '3rem',
            width: { xs: '100%', md: 'auto' },
            justifyContent: { xs: 'center', md: 'flex-start' },
            mb: { xs: 1, md: 0 }
          }}
        >
          <Box component={Link} sx={{ cursor: 'pointer' }} to={'/companies'}>
            <img
              src={logo}
              alt='Sotecteval-Tec logo'
              style={{
                width: '10.682rem',
                height: '4.179rem'
              }}
            />
          </Box>
          <FundingLogos size='small' />
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: { xs: '100%', md: 'auto' },
            justifyContent: { xs: 'center', md: 'flex-end' }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '2.5rem',
              height: '2.5rem'
            }}
          >
            <IconButton onClick={handleOpenNotificationsMenu}>
              <Badge
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                color='warning'
                badgeContent={notifications?.totalUnread || 0}
              >
                <NotificationsOutlinedIcon
                  fontSize='large'
                  sx={{
                    color: isNotificationsMenuOpen
                      ? 'primary.main'
                      : 'custom.semiTransparentWhite'
                  }}
                />
              </Badge>
            </IconButton>
            <NotificationsMenu
              anchorEl={notificationsAnchorEl}
              isOpen={isNotificationsMenuOpen}
              handleCloseMenu={handleCloseNotificationsMenu}
            />
          </Box>
          <Stack
            direction='row'
            spacing='.75rem'
            alignItems='center'
            sx={{
              padding: '0.5rem 1rem'
            }}
          >
            {user.avatarUri ? (
              <ContainedAvatar src={user.avatarUri} />
            ) : (
              <Avatar>{getAvatarName(user.name!, user.lastName!)}</Avatar>
            )}

            <Box onClick={handleClick} sx={{ cursor: 'pointer' }}>
              <Stack direction='column' justifyContent='center'>
                <Typography
                  variant='body1'
                  sx={{
                    color: '#B0BEC5'
                  }}
                >
                  {user.name} {user.lastName}
                </Typography>
                <Typography variant='body2' sx={{ color: '#B0BEC5' }}>
                  {user.email}
                </Typography>
              </Stack>
            </Box>

            <Menu
              anchorEl={anchorEl}
              id='account-menu'
              open={open}
              onClose={handleClose}
              slotProps={{
                paper: {
                  elevation: 8,
                  sx: {
                    overflow: 'visible',
                    width: '11.875rem',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))'
                  }
                }
              }}
            >
              <MenuItem component={Link} to={'/profile'} onClick={handleClose}>
                <ListItemIcon>
                  <PersonIcon fontSize='small' />
                </ListItemIcon>
                {t('account.profile')}
              </MenuItem>
              <MenuItem
                onClick={() => signOutMutation.mutate(accessToken!)}
                disabled={signOutMutation.isPending}
              >
                <ListItemIcon>
                  <Logout fontSize='small' />
                </ListItemIcon>
                {signOutMutation.isPending
                  ? t('sign_out_loading')
                  : t('sign_out')}
              </MenuItem>
            </Menu>
          </Stack>
        </Box>
      </Box>
    </Paper>
  )
}
